import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Grid, TextField } from "@mui/material";
import { isRejected } from "@reduxjs/toolkit";
import convertEmailToUsername from "helpers/convertEmailToUsername";
import getErrorDetailTranslationKey from "helpers/errors/getErrorDetailTranslationKey";
import useAlertBox from "hooks/useAlertBox";
import useFormValidation from "hooks/useFormValidation";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "store";
import { forgotPassword } from "store/slices/auth/authSlice";
import { isAxiosErrorOfResponseType } from "types/helpers";
import * as yup from "yup";

const forgotPasswordSchema = yup.object({
  email: yup.string().required().email(),
});

type ForgotPasswordInput = yup.InferType<typeof forgotPasswordSchema>;

export default function ForgotPasswordForm() {
  const { t } = useTranslation(["auth", "errors"]);
  const dispatch = useDispatch();

  const [isRequestPending, setIsRequestPending] = useState(false);

  const { AlertBox, setAlert, clearAlert } = useAlertBox();

  const { handleSubmit, formState, register } = useForm<ForgotPasswordInput>({
    resolver: yupResolver(forgotPasswordSchema),

    mode: "onBlur",
    reValidateMode: "onBlur",
  });

  const registerValidation = useFormValidation(formState, { ns: "auth" });

  const handleResetPassword = handleSubmit(({ email }) => {
    setIsRequestPending(true);
    clearAlert();

    const username = convertEmailToUsername(email);

    dispatch(
      forgotPassword({
        username,
      })
    ).then((action) => {
      setIsRequestPending(false);

      if (isRejected(action)) {
        if (isAxiosErrorOfResponseType(action.payload) && action.payload.response) {
          const responseData = action.payload.response.data;

          if (responseData.detail) {
            const errorMessageTranslationKey = getErrorDetailTranslationKey(responseData.detail);

            setAlert({
              message: t(errorMessageTranslationKey),
              severity: "error",
            });
          }
        }
      } else {
        setAlert({
          message: t("emailWasSent", {
            email,
          }),
          severity: "success",
        });
      }
    });
  });

  return (
    <>
      <Grid item xs={12}>
        <AlertBox />
      </Grid>

      <Grid item xs={12} container spacing={3} component="form" onSubmit={handleResetPassword}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t("email")}
            placeholder={t("emailPlaceholder")}
            {...register("email")}
            {...registerValidation("email")}
          />
        </Grid>

        <Grid item xs={12} mt={3}>
          <LoadingButton fullWidth type="submit" loading={isRequestPending} size="medium">
            {t("resetPassword")}
          </LoadingButton>
        </Grid>
      </Grid>
    </>
  );
}
